// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const { useTranslation } = require('react-i18next');
const PropTypes = require('prop-types');
const classnames = require('classnames');
const { default: Icon } = require('@stremio/stremio-icons/react');
const { Modal, useRouteFocused } = require('stremio-router');
const { useServices } = require('stremio/services');
const { Button, Image } = require('stremio/components');
const CredentialsTextInput = require('./CredentialsTextInput');

const styles = require('./styles');

const LOGIN_FORM = 'login';
const STREMLINK_WEB_AUTH_ENDPOINT = 'https://stremlink.com/wp-json/stremlink/v1/web-auth';

const Intro = ({ queryParams }) => {
    const { core } = useServices();
    const { t } = useTranslation();
    const routeFocused = useRouteFocused();
    const emailRef = React.useRef(null);
    const passwordRef = React.useRef(null);
    const errorRef = React.useRef(null);
    const autoLoginAttemptedRef = React.useRef(false);
    const [loaderModalOpen, setLoaderModalOpen] = React.useState(false);
    const [state, dispatch] = React.useReducer(
        (state, action) => {
            switch (action.type) {
                case 'change-credentials':
                    return {
                        ...state,
                        error: '',
                        [action.name]: action.value
                    };
                case 'toggle-checkbox':
                    return {
                        ...state,
                        error: '',
                        [action.name]: !state[action.name]
                    };
                case 'error':
                    return {
                        ...state,
                        error: action.error
                    };
                default:
                    return state;
            }
        },
        {
            form: LOGIN_FORM,
            email: '',
            password: '',
            error: ''
        }
    );
    const loginWithEmail = React.useCallback(() => {
        if (typeof state.email !== 'string' || state.email.length === 0 || !emailRef.current.validity.valid) {
            dispatch({ type: 'error', error: t('INVALID_EMAIL') });
            return;
        }
        if (typeof state.password !== 'string' || state.password.length === 0) {
            dispatch({ type: 'error', error: t('INVALID_PASSWORD') });
            return;
        }
        setLoaderModalOpen(true);
        core.transport.dispatch({
            action: 'Ctx',
            args: {
                action: 'Authenticate',
                args: {
                    type: 'Login',
                    email: state.email,
                    password: state.password
                }
            }
        });
    }, [state.email, state.password]);
    const emailOnChange = React.useCallback((event) => {
        dispatch({
            type: 'change-credentials',
            name: 'email',
            value: event.currentTarget.value
        });
    }, []);
    const emailOnSubmit = React.useCallback(() => {
        passwordRef.current.focus();
    }, []);
    const passwordOnChange = React.useCallback((event) => {
        dispatch({
            type: 'change-credentials',
            name: 'password',
            value: event.currentTarget.value
        });
    }, []);
    const passwordOnSubmit = React.useCallback(() => {
        loginWithEmail();
    }, [loginWithEmail]);
    React.useEffect(() => {
        if (routeFocused && typeof state.error === 'string' && state.error.length > 0) {
            errorRef.current.scrollIntoView();
        }
    }, [state.error]);
    React.useEffect(() => {
        if (routeFocused) {
            emailRef.current.focus();
        }
    }, [routeFocused, core]);
    React.useEffect(() => {
        const onCoreEvent = ({ event, args }) => {
            switch (event) {
                case 'UserAuthenticated': {
                    setLoaderModalOpen(false);
                    if (routeFocused) {
                        window.location = '#/';
                    }
                    break;
                }
                case 'Error': {
                    if (args.source.event === 'UserAuthenticated') {
                        setLoaderModalOpen(false);
                    }

                    break;
                }
            }
        };
        core.transport.on('CoreEvent', onCoreEvent);
        return () => {
            core.transport.off('CoreEvent', onCoreEvent);
        };
    }, [routeFocused]);
    React.useEffect(() => {
        if (!routeFocused || autoLoginAttemptedRef.current) {
            return;
        }

        autoLoginAttemptedRef.current = true;
        setLoaderModalOpen(true);

        fetch(STREMLINK_WEB_AUTH_ENDPOINT, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            cache: 'no-store'
        })
            .then((response) => response.ok ? response.json() : null)
            .then((payload) => {
                if (
                    payload &&
                    payload.authenticated === true &&
                    payload.has_credentials === true &&
                    typeof payload.stremio_email === 'string' &&
                    payload.stremio_email.length > 0 &&
                    typeof payload.password === 'string' &&
                    payload.password.length > 0
                ) {
                    core.transport.dispatch({
                        action: 'Ctx',
                        args: {
                            action: 'Authenticate',
                            args: {
                                type: 'Login',
                                email: payload.stremio_email,
                                password: payload.password
                            }
                        }
                    });
                    return;
                }

                setLoaderModalOpen(false);
            })
            .catch(() => {
                setLoaderModalOpen(false);
            });
    }, [routeFocused]);
    return (
        <div className={styles['intro-container']}>
            <div className={styles['background-container']} />
            <div className={styles['heading-container']}>
                <div className={styles['logo-container']}>
                    <Image className={styles['logo']} src={require('/assets/images/stremlink-logo-transparent.svg')} alt={' '} />
                </div>
                <div className={styles['title-container']}>
                    {'We Don’t Do Limits'}
                </div>
            </div>
            <div className={styles['content-container']}>
                <div className={styles['form-container']}>
                    <CredentialsTextInput
                        ref={emailRef}
                        className={styles['credentials-text-input']}
                        type={'email'}
                        placeholder={t('EMAIL')}
                        value={state.email}
                        onChange={emailOnChange}
                        onSubmit={emailOnSubmit}
                    />
                    <CredentialsTextInput
                        ref={passwordRef}
                        className={styles['credentials-text-input']}
                        type={'password'}
                        placeholder={t('PASSWORD')}
                        value={state.password}
                        onChange={passwordOnChange}
                        onSubmit={passwordOnSubmit}
                    />
                    {
                        state.error && state.error.length > 0 ?
                            <div ref={errorRef} className={styles['error-message']}>{state.error}</div>
                            :
                            null
                    }
                    <Button className={classnames(styles['form-button'], styles['submit-button'])} onClick={loginWithEmail}>
                        <div className={styles['label']}>{t('LOG_IN')}</div>
                    </Button>
                    <Button className={classnames(styles['form-button'], styles['signup-form-button'], styles['signup-cta-button'])} href={'https://stremlink.com/#get_started'}>
                        <div className={styles['label']}>{'Sign up to Stremlink'}</div>
                    </Button>
                </div>
            </div>
            {
                loaderModalOpen ?
                    <Modal className={styles['loading-modal-container']}>
                        <div className={styles['loader-container']}>
                            <Icon className={styles['icon']} name={'person'} />
                            <div className={styles['label']}>{t('AUTHENTICATING')}</div>
                            <Button className={styles['button']} onClick={() => setLoaderModalOpen(false)}>
                                {t('BUTTON_CANCEL')}
                            </Button>
                        </div>
                    </Modal>
                    :
                    null
            }
        </div>
    );
};

Intro.propTypes = {
    queryParams: PropTypes.instanceOf(URLSearchParams)
};

module.exports = Intro;
